import {
  CloseOutlined,
  ExportOutlined,
  HistoryOutlined,
  PlusOutlined,
  ReadOutlined,
} from '@ant-design/icons'
import assistantStore from '@stores/assistantStore'
import { observer } from 'mobx-react'

import Composer from './Composer'
import ConversationList from './ConversationList'
import EmailCitationModal from './EmailCitationModal'
import AssistantGuide from './Guide/AssistantGuide'
import MessageList from './MessageList'
import PrivacyNotice from './PrivacyNotice'
import { AssistantAvatar, IconGhostButton, KW } from './ui'

interface Props {
  /** Rendered on the standalone page (/view/assistant) instead of the drawer. */
  standalone?: boolean
}

/**
 * The whole Assistant (header, chat, history, guide) — hosted by the drawer inside the app or by
 * the standalone page in its own browser tab.
 */
const AssistantPanel = ({ standalone = false }: Props) => (
  // data-assistant-panel: the Composer takes the focus back only from inside the panel.
  <div className="flex h-full min-h-0 flex-col" data-assistant-panel>
    <div
      className="flex h-[58px] flex-shrink-0 items-center gap-[11px] px-[14px]"
      style={{ borderBottom: `1px solid ${KW.line}` }}
    >
      {!standalone && (
        <IconGhostButton
          title="Close"
          icon={<CloseOutlined />}
          onClick={assistantStore.close}
        />
      )}
      <div className="flex min-w-0 flex-1 items-center gap-[9px]">
        <AssistantAvatar size={26} />
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-[9px]">
            <span
              className="text-base font-extrabold leading-5"
              style={{ color: KW.ink }}
            >
              K-Assist
            </span>
            <span
              className="rounded-full px-[7px] py-px text-[10px] font-extrabold tracking-wider"
              style={{
                color: KW.teal500,
                background: '#e7f7fb',
                border: `1px solid ${KW.softBorder}`,
              }}
            >
              BETA
            </span>
          </div>
          <span
            className="truncate text-[11px] font-semibold leading-4"
            style={{ color: KW.muted }}
          >
            AI-Powered Intelligence for Kawaconn
          </span>
        </div>
      </div>
      <IconGhostButton
        title="New chat"
        icon={<PlusOutlined />}
        disabled={assistantStore.isTurnRunning}
        onClick={assistantStore.newConversation}
      />
      <IconGhostButton
        title="Guide"
        icon={<ReadOutlined />}
        active={assistantStore.showGuide}
        onClick={assistantStore.toggleGuide}
      />
      <IconGhostButton
        title="History"
        icon={<HistoryOutlined />}
        active={assistantStore.showHistory}
        onClick={assistantStore.toggleHistory}
      />
      {!standalone && (
        <IconGhostButton
          title="Open in new window"
          icon={<ExportOutlined />}
          disabled={assistantStore.isBusy}
          onClick={assistantStore.openInNewWindow}
        />
      )}
    </div>

    {assistantStore.showHistory ? (
      <ConversationList />
    ) : assistantStore.showGuide ? (
      <AssistantGuide standalone={standalone} />
    ) : assistantStore.privacyNotice ? (
      // Read once before the first question; the history and the guide stay within reach.
      <PrivacyNotice />
    ) : (
      <>
        <MessageList />
        <Composer />
      </>
    )}
    <EmailCitationModal />
  </div>
)

export default observer(AssistantPanel)
